import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <a href="/" className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Is It Taxed
            </a>
            <a href="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Card className="border-2">
          <CardContent className="p-8">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

            <div className="space-y-6 text-gray-700">
              <section>
                <h2 className="text-2xl font-bold mb-3">1. Introduction</h2>
                <p className="mb-4">
                  [Your legal advisor will provide this content]
                </p>
                <p>
                  This Privacy Policy describes how Is It Taxed ("we", "us", or "our") collects, uses, and shares your personal information when you use our vehicle tax checking service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">2. Information We Collect</h2>
                <h3 className="text-xl font-semibold mb-2">2.1 Personal Information</h3>
                <p className="mb-4">
                  [Your legal advisor will provide this content]
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account information (name, email address)</li>
                  <li>Vehicle registration numbers searched</li>
                  <li>Usage data and search history</li>
                  <li>Payment information (processed securely by Stripe)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-2 mt-4">2.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Usage statistics</li>
                </ul>

                <h3 className="text-xl font-semibold mb-2 mt-4">2.3 Vehicle Reminder Data (Premium Feature)</h3>
                <p className="mb-2">
                  If you opt-in to our Tax/MOT expiry reminder service, we additionally store:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Vehicle registration number</li>
                  <li>Vehicle make, model, and colour</li>
                  <li>Tax and MOT expiry dates</li>
                  <li>Your reminder preferences (timing)</li>
                  <li>Email address for sending reminders</li>
                </ul>
                <p className="mt-2 text-sm text-gray-600">
                  This data is used solely to send you reminder emails before your vehicle's Tax or MOT expires. 
                  You can unsubscribe from reminders at any time via the link in reminder emails or through your dashboard, 
                  and we will stop sending reminder emails. You can also request complete deletion of this data.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">3. How We Use Your Information</h2>
                <p className="mb-4">
                  [Your legal advisor will provide this content]
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To provide vehicle tax and MOT checking services</li>
                  <li>To manage your account and subscription</li>
                  <li>To send Tax/MOT expiry reminders (if you opt-in)</li>
                  <li>To improve our services</li>
                  <li>To send service-related communications</li>
                  <li>To comply with legal obligations</li>
                </ul>

                <h3 className="text-xl font-semibold mb-2 mt-4">3.1 Reminder Email Communications</h3>
                <p className="mb-2">
                  If you opt-in to the reminder service, we will send you emails:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>At your chosen time before Tax/MOT expiry (10, 8, 6, or 4 weeks)</li>
                  <li>Only for vehicles you have explicitly added to your reminders</li>
                  <li>Each email includes a link to unsubscribe from that specific reminder</li>
                </ul>
                <p className="mt-2 text-sm text-gray-600">
                  You can manage all your reminders or unsubscribe from all at any time from your dashboard.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">4. Data Sharing and Third Parties</h2>
                <p className="mb-4">
                  [Your legal advisor will provide this content]
                </p>
                <p className="mb-2">We share data with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>DVLA API (for vehicle information)</li>
                  <li>Stripe (for payment processing)</li>
                  <li>Google (for authentication)</li>
                  <li>Cloud hosting providers</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">5. Data Retention</h2>
                <p>
                  [Your legal advisor will provide this content]
                </p>
                <p>
                  Search history is automatically deleted after 30 days. Account data is retained while your account is active.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">6. Your Rights (GDPR)</h2>
                <p className="mb-4">
                  [Your legal advisor will provide this content]
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Right to access your data</li>
                  <li>Right to correct inaccurate data</li>
                  <li>Right to delete your data</li>
                  <li>Right to data portability</li>
                  <li>Right to object to processing</li>
                  <li>Right to withdraw consent</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">7. Cookies</h2>
                <p>
                  [Your legal advisor will provide this content]
                </p>
                <p>
                  We use essential cookies for authentication and service functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">8. Data Security</h2>
                <p>
                  [Your legal advisor will provide this content]
                </p>
                <p>
                  We implement appropriate security measures to protect your personal information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">9. Children's Privacy</h2>
                <p>
                  [Your legal advisor will provide this content]
                </p>
                <p>
                  Our service is not intended for children under 16.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">10. Contact Us</h2>
                <p>
                  [Your legal advisor will provide this content]
                </p>
                <p className="mb-2">For privacy-related questions, contact us at:</p>
                <ul className="space-y-1">
                  <li>Email: [YOUR EMAIL]</li>
                  <li>Address: [YOUR ADDRESS]</li>
                </ul>
              </section>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-8">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This is a template structure. Please have a legal professional review and complete this Privacy Policy to ensure compliance with UK GDPR, Data Protection Act 2018, and Google Play Store requirements.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
